import {
  Component,
  Input,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription, forkJoin } from "rxjs";
import { AppService } from "src/app/app.service";
import { CompsDataService } from "src/app/comps-page/comps-data.service";
import {
  DeleteDpParams,
  SetDisableActions,
} from "src/app/store/actions/global-subheader.actions";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { TPRDataService } from "../tpr-data.service";
import { UserTprSettingObject, VsPeerState } from "../tpr.model";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import {
  VAIconRegistryService,
  close,
  check,
  closeCircle,
  infoCircle,
} from "@va/core-library/icons";
import {
  getAddedAndRemovedItems,
  getTickersCompanyId,
  getUserTprSettingDetails,
  updateUserTprSettingDetails,
} from "../tpr-utils";
import {
  TPR_LOG_ACTION_FEATURES,
  TPR_PAGE,
  VS_PEERS_TAB,
} from "../tpr-constants";
import { MARKETDATA_COPYRIGHT_MESSAGE } from "src/app/common-constants";

@Component({
  selector: "app-tpr-peers",
  templateUrl: "./tpr-peers.component.html",
  styleUrls: ["./tpr-peers.component.scss"],
})
export class TprPeersComponent {
  //inputs, outputs
  @Input() primaryTicker = "";
  @Input() isPeerTabVisible = false;

  //variables
  orientation = "grid";
  currentTabId = "peer";
  currentCompanyId = undefined;
  snackbarMessage = "";
  suggestedPeers: Array<any> = [];
  columnPickerAvailableColumns: Array<any> = [];
  columnPickerSelectedColumns: Array<any> = [];
  watchlistData: Array<any> = [];
  recentlyVisitedCompanies: Array<any> = [];
  snackBarRef: MatSnackBarRef<any>;
  // meta is just a placeholder but the grid will be updated whenever _meta is changed
  meta: any;
  _meta: any;
  state: VsPeerState = {};
  fetchedWatchlistData = false;
  watchListAndRecentCompaniesData;
  snackbarInfo: { message: string; type: string } = {
    message: "",
    type: "info",
  };
  columnPickerData = [];
  tprVsPeersColumnPickerItem = [];
  userTprSettingDetails: UserTprSettingObject;
  marketDataCopyRightMessage = MARKETDATA_COPYRIGHT_MESSAGE;

  // Subscription
  globalAppSettingSubscriber: Subscription;
  saveTprColumnDetailSubscriber: Subscription;
  saveTprVsPeersDetailSubscriber: Subscription;

  //loader
  isPeersDataReady = false;
  isMetaInProgress = false;
  isWatchListAndRecentCompDataReady = false;
  isMetaReady = false;
  isUserSettingsInProgress = false;
  isTprColumnDetailsSaveApiInProgress = false;

  ngOnInit() {
    this.globalAppSettingSubscriber = this.store
      .select(
        stateGetter("globalHeader", "globalAppSettings", "period_settings"),
      )
      .subscribe((data) => {
        if (data) {
          //raise update of state on the tabs to trigger data calls
          this.updatevsPeerTabData(this.state);
        }
      });
  }

  constructor(
    private compDataService: CompsDataService,
    private snackBar: MatSnackBar,
    private appService: AppService,
    private store: Store<AppState>,
    private tprDataService: TPRDataService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([close, check, closeCircle, infoCircle]);
  }

  @ViewChild("successTemplate", { static: false })
  successTemplate: TemplateRef<any>;
  @ViewChild("failureTemplate", { static: false })
  failureTemplate: TemplateRef<any>;
  @ViewChild("infoTemplate", { static: false }) infoTemplate: TemplateRef<any>;

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.primaryTicker &&
      changes.primaryTicker.currentValue !== changes.primaryTicker.previousValue
    ) {
      this.getMetaData();
    }
    if (
      changes.isPeerTabVisible &&
      changes.isPeerTabVisible.currentValue !==
        changes.isPeerTabVisible.previousValue
    ) {
      if (changes.isPeerTabVisible.currentValue) {
        this.setMetaValues();
      }
    }
  }

  setMetaValues() {
    this.meta = this._meta;
  }
  /**
   * It is call when this component is destroyed
   * Here we are unsubscribing the subscription, to prevent memory leaks
   */
  ngOnDestroy() {
    if (this.globalAppSettingSubscriber) {
      this.globalAppSettingSubscriber.unsubscribe();
    }
    if (this.saveTprColumnDetailSubscriber) {
      this.saveTprColumnDetailSubscriber.unsubscribe();
    }
    if (this.saveTprVsPeersDetailSubscriber) {
      this.saveTprVsPeersDetailSubscriber.unsubscribe();
    }
  }

  /**
   * Set the snackbar message and snackbarRef
   */
  showSnackBarToastMessage(info) {
    this.snackbarMessage = info.message;
    this.snackBarRef = this.getSnackbarRef(info.type);
  }

  /**
   * Hides the snack bar
   */
  dismissSnackBar() {
    this.snackBarRef.dismiss();
  }

  /**
   * Shows snackbar message, according to (success | info | failure) template
   * @param {object} type - type of message (success | info | failure)
   */
  getSnackbarRef(type) {
    const typeMap = {
      success: this.successTemplate,
      info: this.infoTemplate,
      failure: this.failureTemplate,
    };
    if (typeMap[type]) {
      return this.snackBar.openFromTemplate(typeMap[type], {
        panelClass: type,
        duration: 5000,
        verticalPosition: "top",
      });
    }
    return undefined;
  }

  updatevsPeerTabData(obj) {
    this.state = {
      ...this.state,
      ...obj,
    };
    this.store.dispatch(new DeleteDpParams());
  }

  getMetaData() {
    this.isMetaInProgress = true;
    this.tprDataService.getVsPeersMeta(this.primaryTicker).subscribe((meta) => {
      if (meta && meta.status === "success" && meta.data) {
        this._meta = {
          columns: meta.data.columns,
          colInfo: meta.data.col_info,
          tickerInfo: meta.data.ticker_info,
          apiTypeList: meta.data.api_type_list,
          currency: meta.data.currency,
        };
        this.columnPickerSelectedColumns =
          meta.data.column_picker_selected_columns;
        this.columnPickerAvailableColumns = meta.data.column_picker_all_columns;
        this.isMetaInProgress = false;
        this.isMetaReady = true;
        const formattedTickerInfo = meta.data.ticker_info.map((item) => {
          if (item.ticker === this.primaryTicker) {
            this.currentCompanyId = item.cid;
          }
          return {
            ...item,
            full_name: item.name,
            tickerDeleteDisabled: item.ticker === this.primaryTicker,
          };
        });

        this.getSuggestedTickerData(formattedTickerInfo);
        this.createvsPeerTab(meta.data, formattedTickerInfo);

        if (this.isPeerTabVisible) {
          this.setMetaValues();
        }
      }
    });
  }

  /**
   * It creates vs-peers tab's main state, which contains filters information
   * @param {object} metaData: response that we get from meta api
   * @param {array} updatedTickerInfo: list of formatted tickers
   */
  createvsPeerTab(metaData: any, updatedTickerInfo) {
    this.state = {
      ticker_info: updatedTickerInfo,
      currency: metaData.currency,
      price_performance: metaData.price_performance,
    };
    this.createTprUserDetails();
    this.settingAvailableColumnsForColumnPicker(
      this.columnPickerAvailableColumns,
    );
  }

  createTprUserDetails() {
    this.userTprSettingDetails = getUserTprSettingDetails({
      tabType: this.currentTabId,
      currentTicker: this.primaryTicker,
      tickers: this.state.ticker_info,
      currency: this.state.currency,
      pricePerformance: this.state.price_performance,
      orientation: this.orientation,
    });
  }

  settingAvailableColumnsForColumnPicker(arrayOfColumns) {
    this.tprVsPeersColumnPickerItem = [];
    for (let k = 0; k < arrayOfColumns.length; k++) {
      let obj = {
        id: arrayOfColumns[k],
        name: arrayOfColumns[k],
        isSelected: true,
        isDisabled: false,
        isDraggable: true,
        isRemovable: true,
        index: k,
      };
      if (
        arrayOfColumns[k] === "Ticker" ||
        arrayOfColumns[k] === "Company Name" ||
        arrayOfColumns[k] === "Market Cap" ||
        arrayOfColumns[k] === "Enterprise value"
      ) {
        obj = {
          ...obj,
          isDraggable: false,
          isRemovable: false,
        };
      }
      this.tprVsPeersColumnPickerItem.push(obj);
    }
    this.columnPickerData = this.tprVsPeersColumnPickerItem;
  }

  /**
   * Makes call to get Watchlist and Recently viewed companies data and update them.
   */
  getWatchListAndRecentCompaniesData() {
    this.isWatchListAndRecentCompDataReady = false;
    const primaryTicker = this.primaryTicker;
    this.compDataService.getWatchlists().subscribe((resp) => {
      this.watchListAndRecentCompaniesData = JSON.parse(
        JSON.stringify(resp.data),
      );
      const apiCalls = [this.compDataService.getRecentlyVisitedCompanies()];
      forkJoin(apiCalls).subscribe((res) => {
        this.store.dispatch(new SetDisableActions(false));
        if (res[0] && res[0].status === "success") {
          this.recentlyVisitedCompanies = this.appService.cloneArray(
            res[0].data,
          );
        }
        const recentViewCompanies = this.recentlyVisitedCompanies.map((el) => {
          const company = {
            name: el.name,
            ticker: el.ticker,
            sli_comps_id: el.sli_comps_id || 0,
            mpt: el.mpt,
            isSelected: false,
            cid: el.cid,
            tickerDeleteDisabled: el.ticker === primaryTicker,
          };
          return company;
        });

        const recentViewCompany = {
          watchername: "Recently viewed (up to 30)",
          company_info: recentViewCompanies,
        };

        this.watchListAndRecentCompaniesData.unshift(recentViewCompany);

        for (let i = 0; i < this.watchListAndRecentCompaniesData.length; i++) {
          const parsedWatchlistData = this.watchListAndRecentCompaniesData[
            i
          ].company_info.map((el) => {
            let company = {};
            company = {
              full_name: el.name,
              name: el.name,
              sli_comps_id: el.sli_comps_id || 0,
              ticker: el.ticker,
              mpt: el.mpt,
              isSelected: false,
              cid: el.companyid || el.cid,
              tickerDeleteDisabled: el.ticker === primaryTicker,
            };
            el = company;
            return el;
          });
          this.watchListAndRecentCompaniesData[i].company_info = JSON.parse(
            JSON.stringify(parsedWatchlistData),
          );
        }
        this.getCompleteSuggestedAndWatchlistData();
        this.isWatchListAndRecentCompDataReady = true;
      });
    });
  }

  onPeerChange(updatedTickerInfo) {
    this.isUserSettingsInProgress = true;
    const updatedTickers = updatedTickerInfo.updatedTickers;
    const addedTickers = updatedTickerInfo.addedTicker;
    const initialTickersId = this._meta.tickerInfo.map((item) => item.cid);
    const updatedTickersId = updatedTickers.map((item) => item.cid);
    this.userTprSettingDetails = updateUserTprSettingDetails(
      this.userTprSettingDetails,
      {
        tickers: getTickersCompanyId(updatedTickers),
      },
    );

    this.saveTprVsPeersDetailSubscriber = this.tprDataService
      .saveTprVsPeersDetails(this.userTprSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          const { addedItems, removedItems } = getAddedAndRemovedItems(
            initialTickersId,
            updatedTickersId,
          );
          this.handlePeersChange(updatedTickers);
          // Show toast message once ticker is added to the table
          if (addedTickers && addedTickers.ticker) {
            this.snackbarInfo.message = `${addedTickers.ticker} has been added to the Table.`;
            this.snackbarInfo.type = "success";
            this.showSnackBarToastMessage(this.snackbarInfo);
          }
          this.appService.logUserAction(
            {},
            this.primaryTicker,
            "",
            TPR_LOG_ACTION_FEATURES.peersTickerUpdate,
            {
              selected_tickers: updatedTickersId,
              added_tickers: addedItems,
              removed_tickers: removedItems,
            },
            "",
            TPR_PAGE,
          );
        }
        this.isUserSettingsInProgress = false;
      });
  }

  /**
   * It Updates the ticker_info value of state object with emitted values.
   * It updates the state object according to tab index
   * It updates ticker suggestion list
   * @param {object} tickers: list of selected tickers
   */
  handlePeersChange(tickers: Array<any>) {
    const peers = { ticker_info: tickers };
    this._meta = {
      ...this._meta,
      tickerInfo: tickers,
    };
    this.setMetaValues();
    this.updatevsPeerTabData(peers);
    this.getSuggestedTickerData(tickers);
  }

  handleDeleteTicker(tickerData) {
    this.isUserSettingsInProgress = true;
    const tickers = this.state.ticker_info.filter((_ticker) => {
      return _ticker.ticker !== tickerData.ticker;
    });
    this.userTprSettingDetails = updateUserTprSettingDetails(
      this.userTprSettingDetails,
      {
        tickers: getTickersCompanyId(tickers),
      },
    );

    this.saveTprVsPeersDetailSubscriber = this.tprDataService
      .saveTprVsPeersDetails(this.userTprSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          this.handleDeletePeer(tickers);
          this.snackbarInfo.message = `${tickerData.ticker} has been removed from the table.`;
          this.snackbarInfo.type = "success";
          this.showSnackBarToastMessage(this.snackbarInfo);
        }
        this.isUserSettingsInProgress = false;
      });
  }

  handleDeletePeer(ticker) {
    const peers = { ticker_info: ticker };
    this._meta.tickerInfo = ticker;
    this.updatevsPeerTabData(peers);
    this.getSuggestedTickerData(ticker);
  }

  getSuggestedTickerData(tickersInfo: Array<any>) {
    if (!this.primaryTicker) {
      return;
    }
    this.isPeersDataReady = false;
    const limit = (tickersInfo || []).length + 10;
    const primaryTicker = this.primaryTicker;

    if (!this.fetchedWatchlistData) {
      this.fetchedWatchlistData = true;
      this.getWatchListAndRecentCompaniesData();
    }
    this.compDataService
      .getCompanyPeers([primaryTicker], null, limit, false, true)
      .subscribe((res) => {
        if (res && res.status === "success") {
          const peerInfo = res.peer_info || [];
          const responsePeers =
            peerInfo.reduce(function (result, item) {
              const checkIfSuggestedTickerAlreadyExists = tickersInfo.find(
                (selectedTicker) => selectedTicker.ticker === item.ticker,
              );
              if (!checkIfSuggestedTickerAlreadyExists) {
                const updatedTicker = { ...item, full_name: item.name };
                result.push(updatedTicker);
              }
              return result;
            }, []) || [];

          this.suggestedPeers = responsePeers.slice(0, 10);

          this.getCompleteSuggestedAndWatchlistData();
          this.isPeersDataReady = true;
        }
      });
  }

  /**
   * Creates watchlist data object required for peers filter
   */
  getCompleteSuggestedAndWatchlistData() {
    const suggestedPeers = this.suggestedPeers;
    const watchlistAndRecentlyVisitedCompanies =
      this.watchListAndRecentCompaniesData;
    let result = [];
    if (suggestedPeers && suggestedPeers.length) {
      const peerComps =
        this.suggestedPeers.map((item) => {
          const company = {
            name: item.name,
            full_name: item.name,
            sli_comps_id: item.sli_comps_id || 0,
            ticker: item.ticker,
            mpt: item.mpt,
            isSelected: false,
            cid: item.cid,
            tickerDeleteDisabled: item.ticker === this.primaryTicker,
          };
          return company;
        }) || [];

      const peerData = {
        watchername: "Suggested Peers",
        company_info: peerComps,
      };
      result = result.concat(peerData, watchlistAndRecentlyVisitedCompanies);
    } else {
      result = result.concat(watchlistAndRecentlyVisitedCompanies);
    }
    this.watchlistData = result;
  }

  onCurrencyChange(currencySelected: string) {
    this.isUserSettingsInProgress = true;
    this.userTprSettingDetails = updateUserTprSettingDetails(
      this.userTprSettingDetails,
      {
        currency: currencySelected,
      },
    );

    this.saveTprVsPeersDetailSubscriber = this.tprDataService
      .saveTprVsPeersDetails(this.userTprSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          const currencyObj = {
            item: currencySelected,
            magMsg: response["magnitude_message"],
          };
          this.updateCurrency(currencyObj);
          this.appService.logUserAction(
            {},
            this.primaryTicker,
            "",
            TPR_LOG_ACTION_FEATURES.peersCurrencyUpdate,
            {
              curreny: currencySelected,
            },
            "",
            TPR_PAGE,
          );
        }
        this.isUserSettingsInProgress = false;
      });
  }

  /**
   * This function parses the currency and calls the update method for the respective tab
   * @param e - value of currency
   * @param index - value used to denote the tab
   */
  updateCurrency(e) {
    const currency = {
      currency: e.item,
      magnitudeMsg: e.magMsg,
    };
    this._meta = {
      ...this._meta,
      currency: e.item,
    };
    this.setMetaValues();
    this.updatevsPeerTabData(currency);
  }

  onPricePerformanceChange(optionSelected) {
    this.isUserSettingsInProgress = true;
    this.userTprSettingDetails = updateUserTprSettingDetails(
      this.userTprSettingDetails,
      {
        pricePerformance: optionSelected,
      },
    );
    this.saveTprVsPeersDetailSubscriber = this.tprDataService
      .saveTprVsPeersDetails(this.userTprSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          this.updatePricePerformance(optionSelected);
          this.appService.logUserAction(
            {},
            this.primaryTicker,
            "",
            TPR_LOG_ACTION_FEATURES.peersPricePerformanceUpdate,
            {
              price_performance: optionSelected,
            },
            "",
            TPR_PAGE,
          );
        }
        this.isUserSettingsInProgress = false;
      });
  }

  updatePricePerformance(value) {
    const pricePerformance = {
      price_performance: value,
    };
    this.updatevsPeerTabData(pricePerformance);
    this.getMetaData();
  }

  updateColumnPicker(selectedColumns) {
    this.isTprColumnDetailsSaveApiInProgress = true;
    this.saveTprColumnDetailSubscriber = this.tprDataService
      .saveTprGridDetails(
        VS_PEERS_TAB.columnPickerdimensionName,
        selectedColumns,
      )
      .subscribe((response) => {
        if (response && response.status === "success") {
          this.getMetaData();
          const { addedItems, removedItems } = getAddedAndRemovedItems(
            this.columnPickerSelectedColumns,
            selectedColumns,
          );
          this.appService.logUserAction(
            {},
            this.primaryTicker,
            "",
            TPR_LOG_ACTION_FEATURES.peersColumnPickerUpdate,
            {
              selected_columns: selectedColumns,
              added_columns: addedItems,
              removed_columns: removedItems,
            },
            "",
            TPR_PAGE,
          );
        }
        this.isTprColumnDetailsSaveApiInProgress = false;
      });
  }
}
